import type { ChartCache, GraphKind, IChartLabelParams, LabelPosition, PhaseData } from '../interfaces';
import { AbsoluteShiftValues, GraphKinds } from '../interfaces';

function locateRechartsLabel(): (params: IChartLabelParams) => LabelPosition {
  const cache: ChartCache = {};

  return (params: IChartLabelParams): LabelPosition => {
    const { phasesData, phase, graphKind, isExpanded } = params;
    const currentDotIndex: number = phasesData.map((dot: PhaseData) => dot.phase).indexOf(phase);
    const [leftDot, currentDot, rightDot] = getGraphDotNeighbours(phasesData, currentDotIndex);
    const hasBottomCase: boolean = isBottomCase(leftDot, currentDot, rightDot);
    const hasEdgeCase: boolean = isEdgeCase(leftDot, currentDot, rightDot, graphKind);

    const key = `
      ${graphKind}-
      ${currentDotIndex}-
      ${hasBottomCase ? 'bottom' : 'no_bottom'}-
      ${isExpanded ? 'exp' : 'col'}-
      ${phase}-
      ${hasEdgeCase ? 'edge' : 'non_edge'}`;

    if (key in cache) {
      return cache[key];
    }

    const result: LabelPosition = getLabelPosition(graphKind, currentDotIndex, hasBottomCase, isExpanded, hasEdgeCase);
    cache[key] = result;
    return result;
  };
}

function getLabelPosition(
  graphKind: GraphKind,
  currentDotIndex: number,
  hasBottomCase: boolean,
  isExpanded: boolean,
  hasEdgeCase: boolean
): LabelPosition {
  switch (true) {
    case currentDotIndex === 0 && graphKind === GraphKinds.RVVolume && isExpanded:
      return 'right';
    case currentDotIndex === 0:
      return 'left';
    case hasBottomCase:
      return 'bottom';
    case !isExpanded:
      return 'top';
    case hasEdgeCase:
      return currentDotIndex !== 1 ? 'left' : 'right';
    default:
      return 'top';
  }
}

function getGraphDotNeighbours(data: Array<PhaseData>, currentPhaseIndex: number): Array<number> {
  const leftDot: number = getPhaseNeighbour(data, currentPhaseIndex - 1);
  const currentDot: number = getPhaseNeighbour(data, currentPhaseIndex);
  const rightDot: number = getPhaseNeighbour(data, currentPhaseIndex + 1);
  return [leftDot, currentDot, rightDot];
}

function getPhaseNeighbour(data: Array<PhaseData>, index: number): number {
  return data[index]?.weight ?? null;
}

function isDescendingCurve(leftDot: number, rightDot: number): boolean {
  return leftDot > rightDot;
}

function isAscendingCurve(leftDot: number, rightDot: number): boolean {
  return leftDot < rightDot;
}

function areEqualDots(leftDot: number, rightDot: number): boolean {
  return leftDot === rightDot;
}

function isOscillatingTurn(leftDot: number, currentDot: number, rightDot: number): boolean {
  return isDescendingCurve(leftDot, currentDot) && isAscendingCurve(currentDot, rightDot);
}

function isLinearToAscendingTurn(leftDot: number, currentDot: number, rightDot: number): boolean {
  return areEqualDots(leftDot, currentDot) && isAscendingCurve(currentDot, rightDot);
}

function isDescendingToLinearTurn(leftDot: number, currentDot: number, rightDot: number): boolean {
  return isDescendingCurve(leftDot, currentDot) && areEqualDots(currentDot, rightDot);
}

function isLinearlyDescending(leftDot: number, currentDot: number, rightDot: number): boolean {
  return isDescendingCurve(leftDot, currentDot) && isDescendingCurve(currentDot, rightDot);
}

function isLinearlyAscending(leftDot: number, currentDot: number, rightDot: number): boolean {
  return isAscendingCurve(leftDot, currentDot) && isAscendingCurve(currentDot, rightDot);
}

function isEdgeCase(leftDot: number, currentDot: number, rightDot: number, graphKind: GraphKind): boolean {
  const absoluteShiftValue: AbsoluteShiftValues =
    graphKind === GraphKinds.RVVolume ? AbsoluteShiftValues.RVVolume : AbsoluteShiftValues.AnnulusSize;

  const optimalDeltaShiftValue: number = absoluteShiftValue / 2;
  const backDeltaShift: number = leftDot - currentDot;
  const frontDeltaShift: number = rightDot - currentDot;

  return (
    (isLinearlyAscending(leftDot, currentDot, rightDot) && frontDeltaShift > optimalDeltaShiftValue) ||
    (isLinearlyDescending(leftDot, currentDot, rightDot) && backDeltaShift > optimalDeltaShiftValue)
  );
}

function isBottomCase(leftDot: number, currentDot: number, rightDot: number): boolean {
  return (
    isOscillatingTurn(leftDot, currentDot, rightDot) ||
    isLinearToAscendingTurn(leftDot, currentDot, rightDot) ||
    isDescendingToLinearTurn(leftDot, currentDot, rightDot)
  );
}

export const memoGetReferenceDotLabelPosition = locateRechartsLabel();
